/*
 * BS 3D piece viewer (demo, 15 Oct 2026; docs/BUILDER.md "3D viewer"). Swirl CAD designs: design + seats
 * attributes (19 Oct 2026; docs/SWIRL.md).
 * Live WebGL renders: metals are physically based, stones use a faceted refraction shader with fire (per-channel
 * refraction), so shine and colour come from light, not from drawn gradients. The five pieces are PLACEHOLDER models
 * built in code until Beautiful Selection's CAD files arrive. A CAD file loads with data-model="url" or by dropping a
 * .glb/.gltf/.stl on the viewer (data-allow-drop): meshes named stone/gem/diamond/cz/moissanite get the stone material,
 * everything else the chosen metal.
 * Units are millimetres. Plain custom elements, no Horizon imports. three.js r168 is vendored as assets/bs-vendor-*.
 */
import * as THREE from './bs-vendor-three.module.min.js';
import { OrbitControls } from './bs-vendor-three-orbit-controls.js';

// Metal colours are approximate reflectance colours (sRGB); roughness gives the polish. Ids match the builder's
// metals (snippets/bs-builder-demo-data.liquid). head = a second metal for the head (two tone: silver band, gold head).
export const METALS = {
  // 20 Oct 2026: polished (rough 0.05-0.08; the stock photos are mirror bright); golds paler, as the 9ct in the stock photos
  // (Justin: "metal colours in the designer needs adjusting"; the earlier ones rendered mustard), under the jewellery
  // studio (metalStudio) and neutral tone mapping
  '925': { label: 'Sterling silver', color: '#eef0f2', rough: 0.05 },
  '925-yg': { label: 'Silver, yellow gold plated', color: '#ecd29a', rough: 0.07 },
  '925-rg': { label: 'Silver, rose gold plated', color: '#ecc4b2', rough: 0.07 },
  'two-tone': { label: 'Two tone', color: '#eef0f2', rough: 0.05, head: '#e9d093' },
  '9yg': { label: '9ct yellow gold', color: '#e9d093', rough: 0.07 },
  '9rg': { label: '9ct rose gold', color: '#eac5b4', rough: 0.07 },
  '9wg': { label: '9ct white gold', color: '#e6e6e2', rough: 0.06 },
  '18yg': { label: '18ct yellow gold', color: '#efcb80', rough: 0.06 },
  '18wg': { label: '18ct white gold', color: '#e8e6e0', rough: 0.06 },
  '18rg': { label: '18ct rose gold', color: '#ebb39b', rough: 0.06 },
  'pt': { label: 'Platinum', color: '#d4d5d8', rough: 0.09 },
  // Swirl two tone: sterling silver shank, 9ct gold insert (the CAD's second part)
  'tt-9yg': { label: 'Sterling silver + 9ct yellow gold', color: '#eef0f2', rough: 0.05, head: '#e9d093' },
  'tt-9rg': { label: 'Sterling silver + 9ct rose gold', color: '#eef0f2', rough: 0.05, head: '#eac5b4' },
  'tt-9wg': { label: 'Sterling silver + 9ct white gold', color: '#eef0f2', rough: 0.05, head: '#e6e6e2' },
  // 9ct white gold band, 9ct yellow or rose gold swirl (Justin, 20 Oct 2026)
  '9wg-9yg': { label: '9ct white gold + 9ct yellow gold', color: '#e6e6e2', rough: 0.06, head: '#e9d093' },
  '9wg-9rg': { label: '9ct white gold + 9ct rose gold', color: '#e6e6e2', rough: 0.06, head: '#eac5b4' },
  '18wg-18yg': { label: '18ct white gold + 18ct yellow gold', color: '#e8e6e0', rough: 0.06, head: '#efcb80' },
  '18wg-18rg': { label: '18ct white gold + 18ct rose gold', color: '#e8e6e0', rough: 0.06, head: '#ebb39b' },
};

// ior = refractive index; spread = fire (index spread across red/green/blue); body = how strongly the colour shows;
// density = how deep the colour gets through thicker parts; boost = brightness. Ids match the builder's stones.
// A colour passed in the color attribute (the builder's colour choice) replaces the stone's own colour.
export const STONES = {
  'cz': { label: 'Cubic zirconia', color: '#ffffff', ior: 2.15, spread: 0.06, body: 0, boost: 1.15 },
  'moissanite': { label: 'Moissanite', color: '#fbfdff', ior: 2.65, spread: 0.1, body: 0, boost: 1.2 },
  'lab-diamond': { label: 'Lab Diamond', color: '#ffffff', ior: 2.42, spread: 0.05, body: 0, boost: 1.2 },
  'natural-diamond': { label: 'Natural diamond', color: '#ffffff', ior: 2.42, spread: 0.05, body: 0, boost: 1.2 },
  'sapphire': { label: 'Blue sapphire', color: '#0f3fa6', ior: 1.77, spread: 0.012, body: 0.95, boost: 0.9, density: 4.5 },
  'emerald': { label: 'Emerald', color: '#03684f', ior: 1.58, spread: 0.01, body: 1, boost: 0.5, density: 1.6 },
  'ruby': { label: 'Ruby', color: '#a3081f', ior: 1.77, spread: 0.012, body: 1, boost: 0.62, density: 4.5 },
  'tanzanite': { label: 'Tanzanite', color: '#8448e0', ior: 1.69, spread: 0.012, body: 0.9, boost: 0.8, density: 0.45 },
  'amethyst': { label: 'Amethyst', color: '#8a3fc4', ior: 1.54, spread: 0.01, body: 0.85, boost: 0.95, density: 0.5 },
  'garnet': { label: 'Garnet', color: '#7d1325', ior: 1.76, spread: 0.012, body: 1, boost: 0.6, density: 4 },
  'peridot': { label: 'Peridot', color: '#9cc23a', ior: 1.65, spread: 0.012, body: 0.9, boost: 0.85, density: 1.5 },
  'topaz': { label: 'Topaz', color: '#7fc3e8', ior: 1.62, spread: 0.01, body: 0.85, boost: 1, density: 1.4 },
  'morganite': { label: 'Morganite', color: '#f0b4a0', ior: 1.59, spread: 0.01, body: 0.7, boost: 1.1, density: 0 },
};

// Pieces the 3D viewer can build itself (until the CAD files arrive). Ids are used by the builder's mapping.
export const ITEMS = {
  'solitaire': 'Solitaire ring',
  'halo': 'Halo ring',
  'three-stone': 'Three stone ring',
  'pendant': 'Solitaire pendant',
  'halo-pendant': 'Halo pendant',
  'studs': 'Stud earrings',
  'halo-studs': 'Halo studs',
};
// Round brilliant proportions (girdle diameter 1): table 57%, crown 15%, pavilion 43%. Shared by the mesh and shader.
const BRILLIANT = { table: 0.285, crown: 0.15, girdle: 0.015, pavilion: 0.43 };

const GEM_VERT = /* glsl */`
  varying vec3 vWorldPos;
  varying vec3 vLocal;
  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    vLocal = position;
    gl_Position = projectionMatrix * viewMatrix * wp;
  }`;

// Faceted stone, traced through its real facets (20 Oct 2026). A stone is convex, so it is the set of its facet planes
// (stone space, girdle width 1; gemPlanes below): light enters the facet it hits, crosses the stone to the facet it
// leaves by (the nearest plane ahead), and either leaves there or reflects back in (total internal reflection), up to
// five times. Light leaving through the pavilion (the underside) sees the setting and the finger: dark. That contrast
// is what makes a stone read as crisp black-and-white facets. Each colour channel uses its own refractive index
// (fire). Works for every cut: round brilliant, princess, emerald step cut, from the stone's own geometry.
const GEM_MAXP = 128;
const GEM_FRAG = /* glsl */`
  uniform mat4 modelMatrix;
  uniform samplerCube envMap;
  uniform vec3 tint;
  uniform float ior;
  uniform float spread;
  uniform float body;
  uniform float boost;
  uniform float density;
  uniform float fireOn;
  uniform vec4 planes[${GEM_MAXP}];
  uniform int nPlanes;
  varying vec3 vWorldPos;
  varying vec3 vLocal;
  vec3 env(vec3 d) { return textureCube(envMap, d).rgb; }
  vec3 toWorld(vec3 d) { return normalize(mat3(modelMatrix) * d); }
  // nearest facet ahead of p along d (from inside): distance, and that facet's outward normal
  float exitFacet(vec3 p, vec3 d, out vec3 n) {
    float best = 1e9; n = vec3(0.0, 1.0, 0.0);
    for (int i = 0; i < ${GEM_MAXP}; i++) {
      if (i >= nPlanes) break;
      vec4 pl = planes[i];
      float dn = dot(pl.xyz, d);
      if (dn > 1e-5) { float t = (pl.w - dot(pl.xyz, p)) / dn; if (t < best) { best = t; n = pl.xyz; } }
    }
    return best;
  }
  // light leaving the stone (rgb) and the path length inside it (a), for colour absorption
  vec4 trace(vec3 p, vec3 d, float n) {
    float len = 0.0;
    for (int i = 0; i < 5; i++) {
      vec3 fn;
      float t = max(exitFacet(p, d, fn), 0.0);
      p += d * t; len += t;
      vec3 o = refract(d, -fn, n);
      if (dot(o, o) > 0.0) {
        vec3 w = env(toWorld(o));
        // out through the pavilion: the polished setting behind it, softer than the studio (0.2 here made every table
        // near-black, second review 20 Oct; under a real stone is bright metal); out through the crown: the studio
        return vec4(fn.y < -0.05 ? mix(w * 0.3, vec3(0.85), 0.45) : w, len);
      }
      d = reflect(d, fn);
      p += d * 1e-4;
    }
    return vec4(env(toWorld(d)) * 0.4, len);
  }
  void main() {
    vec3 Nw = normalize(cross(dFdx(vWorldPos), dFdy(vWorldPos)));
    vec3 Vw = normalize(vWorldPos - cameraPosition);
    if (dot(Nw, Vw) > 0.0) Nw = -Nw;
    vec3 vL = normalize(inverse(mat3(modelMatrix)) * Vw);
    vec3 nL = normalize(cross(dFdx(vLocal), dFdy(vLocal)));
    if (dot(nL, vL) > 0.0) nL = -nL;
    float cosi = clamp(dot(-Vw, Nw), 0.0, 1.0);
    float f0 = pow((ior - 1.0) / (ior + 1.0), 2.0);
    float F = f0 + (1.0 - f0) * pow(1.0 - cosi, 5.0);
    vec3 refl = env(reflect(Vw, Nw));
    vec4 base = trace(vLocal, normalize(refract(vL, nL, 1.0 / ior)), ior);
    vec3 col = base.rgb;
    // fire: each colour along its own path (centre stones; small stones are too small to show it)
    if (fireOn > 0.5) {
      vec3 fire;
      for (int c = 0; c < 3; c++) {
        float n = ior + (float(c) - 1.0) * spread;
        fire[c] = trace(vLocal, normalize(refract(vL, nL, 1.0 / n)), n)[c];
      }
      col = mix(col, fire, 0.6);
    }
    // coloured stones absorb along the path (Beer-Lambert): thicker parts are deeper in colour
    vec3 tintN = tint / max(max(tint.r, tint.g), max(tint.b, 1e-3));
    vec3 absorb = exp(-(vec3(1.0) - tintN) * (0.6 + base.a) * density);
    col *= mix(vec3(1.0), absorb * tintN, body);
    col *= boost;
    // pinpoint glints: only the studio's bright lights (above 1.0) flash off the facets
    vec3 glint = max(refl - vec3(1.0), vec3(0.0)) * 0.6;
    gl_FragColor = vec4(mix(col, refl, F) + glint, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }`;

// ---------- stone shapes ----------
// Each shape is an outline seen from above, as a radius for every direction (width across = 1, so a round stone has
// radius 0.5), how long it is (X), where its prongs go, and its cut. k = width of 1 ct of this shape as a fraction
// of a 1 ct round. Outlines are centred on their bounding box and convex, so one radius per direction is exact.
function polarOutline(points) {
  const xs = points.map((p) => p[0]), cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const pol = points.map(([x, z]) => [Math.atan2(z, x - cx), Math.hypot(x - cx, z)]).sort((a, b) => a[0] - b[0]);
  return (a) => {
    a = Math.atan2(Math.sin(a), Math.cos(a));
    let i = pol.findIndex((p) => p[0] >= a);
    if (i <= 0) i = i === 0 ? 0 : pol.length;
    const p0 = pol[(i - 1 + pol.length) % pol.length], p1 = pol[i % pol.length];
    let a0 = p0[0], a1 = p1[0];
    if (a1 < a0) a1 += Math.PI * 2;
    let aa = a;
    if (aa < a0) aa += Math.PI * 2;
    const t = a1 - a0 < 1e-9 ? 0 : (aa - a0) / (a1 - a0);
    return p0[1] + (p1[1] - p0[1]) * t;
  };
}
const sample = (n, f) => Array.from({ length: n }, (_, i) => f((i / n) * Math.PI * 2));
const SHAPES = {
  round: { k: 1, length: 1, cut: 'brilliant', prongs: null, r: () => 0.5 },
  oval: { k: 0.88, length: 1.3, cut: 'brilliant', prongs: [45, 135, 225, 315], r: polarOutline(sample(240, (t) => [0.65 * Math.cos(t), 0.5 * Math.sin(t)])) },
  // a square cushion: a rounded square (superellipse)
  cushion: { k: 0.95, length: 1, cut: 'brilliant', prongs: [45, 135, 225, 315], r: polarOutline(sample(240, (t) => {
    const c = Math.cos(t), s = Math.sin(t), n = 3.4;
    return [0.5 * Math.sign(c) * Math.abs(c) ** (2 / n), 0.5 * Math.sign(s) * Math.abs(s) ** (2 / n)];
  })) },
  // princess: a square brilliant with sharp corners (Swirl: Agnidelia, Agnidoo)
  princess: { k: 0.89, length: 1, cut: 'brilliant', prongs: [45, 135, 225, 315], r: polarOutline(sample(240, (t) => {
    const c = Math.cos(t), s = Math.sin(t), m = Math.max(Math.abs(c), Math.abs(s));
    return [0.5 * c / m, 0.5 * s / m];
  })) },
  // pear: a round end and a point (the round end's hull with the tip), point along +X. About 1.58 long by 1 wide
  // (was 1.45 and read as an egg; Justin, 15 Oct 2026)
  pear: { k: 0.78, length: 1.58, cut: 'brilliant', prongs: [0, 105, 165, 195, 255], r: polarOutline((() => {
    const R = 0.5, d = 1.08, phi = Math.acos(R / d), pts = [];
    for (let i = 0; i <= 200; i++) { const a = phi + (i / 200) * (2 * Math.PI - 2 * phi); pts.push([R * Math.cos(a), R * Math.sin(a)]); }
    // the two straight sides into the point, sampled along their length: with the point alone, the radius was
    // interpolated across the gap and the point came out rounded
    const tx = R * Math.cos(phi), tz = R * Math.sin(phi);
    for (let j = 1; j < 60; j++) { const t = j / 60; pts.push([tx + (d - tx) * t, tz * (1 - t)], [tx + (d - tx) * t, -tz * (1 - t)]); }
    pts.push([d, 0]);
    return pts;
  })()) },
  // emerald: a step-cut rectangle with cut corners, 1.4 long
  emerald: { k: 0.8, length: 1.4, cut: 'step', prongs: [33, 147, 213, 327], r: polarOutline((() => {
    const A = 0.7, B = 0.5, c = 0.13;
    return [[A, B - c], [A - c, B], [-A + c, B], [-A, B - c], [-A, -B + c], [-A + c, -B], [A - c, -B], [A, -B + c]]
      .flatMap((p, i, arr) => { const q = arr[(i + 1) % arr.length]; return Array.from({ length: 30 }, (_, j) => [p[0] + (q[0] - p[0]) * j / 30, p[1] + (q[1] - p[1]) * j / 30]); });
  })()) },
};
export const SHAPE_IDS = Object.keys(SHAPES);
// outline factor: how far this shape reaches in direction a, relative to a round stone (1 for round)
const reach = (shape, a) => 2 * SHAPES[shape].r(a);

// Brilliant cut on any outline (girdle width 1, table up +Y, girdle centre at the origin): 8 star, 16 bezel halves,
// 16 upper girdle, girdle, 32 lower girdle and 16 pavilion facets. Each ring of facet corners follows the outline.
function brilliantGeometry(shape = 'round') {
  const { table, crown, girdle, pavilion } = BRILLIANT;
  const v = (a, r, y) => { const f = reach(shape, a); return new THREE.Vector3(Math.cos(a) * r * f, y, Math.sin(a) * r * f); };
  const T = [], S = [], Gt = [], Gb = [], Pm = [], P = [];
  const yT = girdle + crown;
  for (let k = 0; k < 8; k++) {
    T.push(v(k * Math.PI / 4, table, yT));
    S.push(v(Math.PI / 8 + k * Math.PI / 4, 0.4, girdle + crown * 0.5));
  }
  for (let j = 0; j < 16; j++) {
    Gt.push(v(j * Math.PI / 8, 0.5, girdle));
    Gb.push(v(j * Math.PI / 8, 0.5, -girdle));
    Pm.push(v((j + 0.5) * Math.PI / 8, 0.3, -girdle - pavilion * 0.42));
  }
  const top = new THREE.Vector3(0, yT, 0), culet = new THREE.Vector3(0, -girdle - pavilion, 0);
  const tri = (a, b, c) => P.push(a, b, c);
  for (let k = 0; k < 8; k++) {
    const k1 = (k + 1) % 8, km = (k + 7) % 8;
    tri(top, T[k], T[k1]);
    tri(T[k], S[k], T[k1]);
    tri(T[k], S[km], Gt[2 * k]);
    tri(T[k], Gt[2 * k], S[k]);
    tri(S[k], Gt[2 * k], Gt[2 * k + 1]);
    tri(S[k], Gt[2 * k + 1], Gt[(2 * k + 2) % 16]);
  }
  for (let j = 0; j < 16; j++) {
    const j1 = (j + 1) % 16;
    tri(Gt[j], Gb[j], Gb[j1]);
    tri(Gt[j], Gb[j1], Gt[j1]);
    tri(Gb[j], Gb[j1], Pm[j]);
    tri(Pm[j], Gb[j1], Pm[j1]);
    tri(culet, Pm[j], Pm[j1]);
  }
  const g = new THREE.BufferGeometry().setFromPoints(P);
  g.computeVertexNormals();
  return g;
}

// Step cut (emerald): flat table, two crown steps, girdle, three pavilion steps to a short keel. Rings follow the
// outline, so the long sides are flat parallel facets and the cut corners their own small facets.
function stepGeometry(shape = 'emerald') {
  const { crown, girdle, pavilion } = BRILLIANT;
  const N = 48, P = [];
  const rings = [[0.36, girdle + crown], [0.44, girdle + crown * 0.55], [0.5, girdle], [0.5, -girdle],
    [0.38, -girdle - pavilion * 0.35], [0.24, -girdle - pavilion * 0.7], [0.06, -girdle - pavilion]];
  const ang = Array.from({ length: N }, (_, i) => (i / N) * Math.PI * 2);
  const pts = rings.map(([r, y]) => ang.map((a) => { const f = reach(shape, a); return new THREE.Vector3(Math.cos(a) * r * f, y, Math.sin(a) * r * f); }));
  const top = new THREE.Vector3(0, rings[0][1], 0), bottom = new THREE.Vector3(0, rings[rings.length - 1][1], 0);
  for (let i = 0; i < N; i++) {
    const i1 = (i + 1) % N;
    P.push(top, pts[0][i], pts[0][i1]);
    for (let k = 0; k < rings.length - 1; k++) {
      P.push(pts[k][i], pts[k + 1][i], pts[k + 1][i1]);
      P.push(pts[k][i], pts[k + 1][i1], pts[k][i1]);
    }
    P.push(bottom, pts[rings.length - 1][i1], pts[rings.length - 1][i]);
  }
  const g = new THREE.BufferGeometry().setFromPoints(P);
  g.computeVertexNormals();
  return g;
}
// A stone's facets as planes (outward normal, distance from the girdle centre), for the shader: coplanar triangles
// (a step cut's long sides, a kite's two halves) become one facet.
function gemPlanes(g) {
  const pos = g.attributes.position, list = [], a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
  for (let i = 0; i < pos.count; i += 3) {
    a.fromBufferAttribute(pos, i); b.fromBufferAttribute(pos, i + 1); c.fromBufferAttribute(pos, i + 2);
    const n = new THREE.Vector3().subVectors(b, a).cross(new THREE.Vector3().subVectors(c, a));
    if (n.lengthSq() < 1e-14) continue;
    n.normalize();
    let d = n.dot(a);
    if (d < 0) { n.negate(); d = -d; } // the girdle centre is inside: outward planes are the far side
    if (!list.some((p) => p.n.dot(n) > 0.9995 && Math.abs(p.d - d) < 2e-4)) list.push({ n, d });
  }
  const v = new Float32Array(GEM_MAXP * 4);
  list.slice(0, GEM_MAXP).forEach((p, i) => v.set([p.n.x, p.n.y, p.n.z, p.d], i * 4));
  return { v, n: Math.min(list.length, GEM_MAXP), count: list.length };
}
const gemGeometry = (shape) => {
  const g = SHAPES[shape].cut === 'step' ? stepGeometry(shape) : brilliantGeometry(shape);
  g.userData.planes = gemPlanes(g);
  return g;
};
let ROUND_PLANES;
// before a stone draws: its own facets into its material (a material may be shared by stones of different cuts)
function gemPlanesHook(renderer, scene, camera, geometry, material) {
  const pl = geometry.userData.planes || ROUND_PLANES || (ROUND_PLANES = gemPlanes(brilliantGeometry('round')));
  const u = material.uniforms;
  if (!u || !u.planes || material.userData.planes === pl) return;
  u.planes.value = pl.v; u.nPlanes.value = pl.n;
  material.userData.planes = pl;
  material.uniformsNeedUpdate = true;
}

// crown height above the girdle centre at relative radius r (0.5 = girdle): where a prong tip must sit
function crownY(r) {
  const { table, crown, girdle } = BRILLIANT;
  if (r <= table) return girdle + crown;
  return girdle + crown * (0.5 - Math.min(0.5, r)) / (0.5 - table);
}

// What the stones "see": a bright white studio with a few dark panels (the contrast that makes a diamond read as
// crisp black-and-white facets) and small, very bright lights (sparkle). Values above 1 survive in the HDR cube map.
function gemStudio() {
  const s = new THREE.Scene();
  // (20 Oct 2026: a softer grey wall and more dark panels; the all-white studio washed the stones out)
  const wall = new THREE.Mesh(new THREE.SphereGeometry(50, 32, 16), new THREE.MeshBasicMaterial({ color: new THREE.Color(0.71, 0.71, 0.715), side: THREE.BackSide }));
  s.add(wall);
  const panel = (dir, w, h, color) => {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide }));
    m.position.copy(dir).normalize().multiplyScalar(40);
    m.lookAt(0, 0, 0);
    s.add(m);
  };
  const dark = new THREE.Color(0.02, 0.02, 0.025), bright = new THREE.Color(10, 10, 9.6);
  [[1, 0.2, 0.3], [-0.8, 0.5, -0.6], [0.2, -0.4, -1], [-0.6, -0.2, 0.9], [0.1, 1, 0.1], [0.9, -0.6, -0.4], [-0.3, 0.6, 0.8], [0.7, 0.7, -0.3], [-0.9, 0.1, 0.1]]
    .forEach((d) => panel(new THREE.Vector3(...d), 20, 26, dark));
  [[0.6, 0.8, 0.5], [-0.7, 0.7, 0.3], [0.3, 0.5, -0.9], [-0.2, 0.9, -0.5], [0.95, 0.3, -0.2]].forEach((d) => panel(new THREE.Vector3(...d), 6, 6, bright));
  return s;
}

// What the metal reflects: a jewellery photo studio (20 Oct 2026; the generic room left polished silver grey and
// blotchy). A white sweep, lighter above and darker below; long softbox strips either side and overhead (the crisp
// highlight lines along a polished band); dark flags between them (the dark reflections that make polished metal read
// as a mirror, as in the stock photos). Values above 1 are the lights.
function metalStudio() {
  const s = new THREE.Scene();
  const geo = new THREE.SphereGeometry(50, 48, 24), p = geo.attributes.position, col = [];
  for (let i = 0; i < p.count; i++) { const y = p.getY(i) / 50, v = y > 0 ? 0.72 + 0.25 * y : 0.72 + 0.32 * y; col.push(v, v, v * 0.985); }
  geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  s.add(new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.BackSide })));
  const panel = (dir, w, h, c) => {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: new THREE.Color(c, c, c), side: THREE.DoubleSide }));
    m.position.copy(new THREE.Vector3(...dir).normalize().multiplyScalar(40));
    m.lookAt(0, 0, 0);
    s.add(m);
  };
  // lights: two tall strips, a long overhead box, a soft front fill
  panel([1, 0.25, 0.25], 5, 44, 7); panel([-1, 0.3, -0.3], 5, 44, 6); panel([0.1, 1, 0.2], 34, 7, 5); panel([0.3, 0.45, 1], 12, 12, 1.6);
  // dark flags
  // grey, not black: black flags left hard dark smears on flat faces (second review, 20 Oct)
  panel([0.45, 0.1, -1], 16, 40, 0.35); panel([-0.55, 0.05, 1], 12, 40, 0.38);
  return s;
}

function shadowTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const x = c.getContext('2d');
  const g = x.createRadialGradient(64, 64, 4, 64, 64, 64);
  g.addColorStop(0, 'rgba(60,45,30,.45)');
  g.addColorStop(1, 'rgba(60,45,30,0)');
  x.fillStyle = g;
  x.fillRect(0, 0, 128, 128);
  return new THREE.CanvasTexture(c);
}

// Stone width (across, mm) from carat weight: a 1 ct round brilliant is about 6.5 mm across and size grows with the
// cube root of the weight; other shapes of the same weight are narrower (SHAPES[].k) and longer (SHAPES[].length).
export function stoneWidth(ct, shape = 'round') {
  const D = 6.5 * Math.cbrt(Math.max(0.01, +ct || 1)) * (SHAPES[shape] || SHAPES.round).k;
  return Math.min(11, Math.max(1.4, D));
}

// the outline at scale 1 (girdle width 1), sampled once per shape
const outlinePts = {};
const outlineOf = (shape) => outlinePts[shape] || (outlinePts[shape] = Array.from({ length: 720 }, (_, i) => {
  const a = (i / 720) * Math.PI * 2, R = reach(shape, a) / 2;
  return [Math.cos(a) * R, Math.sin(a) * R];
}));

// closed loop of points following an outline: centre at the origin, the outline pushed out by `out` mm.
// Pushed out square to the edge (a true parallel outline, every shape here is convex), not along rays from the
// centre: along rays a pear's straight sides bowed out into a peanut-shaped halo (Justin, 15 Oct 2026).
function outlineLoop(shape, scale, out, y, n = 96) {
  if (!out) {
    return Array.from({ length: n }, (_, i) => {
      const a = (i / n) * Math.PI * 2, R = reach(shape, a) * scale / 2;
      return new THREE.Vector3(Math.cos(a) * R, y, Math.sin(a) * R);
    });
  }
  const pts = outlineOf(shape);
  return Array.from({ length: n }, (_, i) => {
    const b = (i / n) * Math.PI * 2, nx = Math.cos(b), nz = Math.sin(b);
    let best = pts[0], hi = -Infinity;
    for (const p of pts) { const h = p[0] * nx + p[1] * nz; if (h > hi) { hi = h; best = p; } }
    return new THREE.Vector3(best[0] * scale + nx * out, y, best[1] * scale + nz * out);
  });
}

// Builds the placeholder pieces. Everything that holds a stone is placed from that stone's own size and outline, so
// stones always sit in their prongs, halo and gallery. Two tone: the band is metal, the head (prongs, halo) is gold.
class PieceBuilder {
  constructor(viewer) {
    this.v = viewer;
    this.metal = viewer.metalMaterial;
    this.headMetal = viewer.headMaterial;
    const sh = viewer.getAttribute('shape');
    this.shape = SHAPES[sh] ? sh : 'round';
    this.ct = +viewer.getAttribute('ct') || 0;
    this.sideCt = +viewer.getAttribute('side-ct') || 0;
    this.accent = +viewer.getAttribute('accent-mm') || 0;
  }
  width(fallbackCt, ct = this.ct) { return stoneWidth(ct || fallbackCt, this.shape); }
  stone(D, mat, shape = this.shape) {
    const m = new THREE.Mesh(this.v.gemGeometry(shape), mat);
    m.onBeforeRender = gemPlanesHook;
    m.scale.setScalar(D);
    m.userData.gem = true;
    return m;
  }
  tube(points, r, mat = this.headMetal, closed = false) {
    const curve = new THREE.CatmullRomCurve3(points, closed);
    return new THREE.Mesh(new THREE.TubeGeometry(curve, closed ? points.length * 2 : 32, r, 10, closed), mat);
  }
  ball(p, r, mat = this.headMetal) {
    const m = new THREE.Mesh(new THREE.SphereGeometry(r, 16, 12), mat);
    m.position.copy(p);
    return m;
  }
  // A stone in a prong head; origin = girdle centre, table up +Y, long axis along X. D = width across.
  // baseY = where the prongs end (into the band or the back of the pendant).
  head(D, { prongs = 4, baseY = -D * 0.6, halo = 0, mat = this.v.gemMaterial() } = {}) {
    const g = new THREE.Group(), shape = this.shape;
    g.add(this.stone(D, mat));
    const pr = Math.max(0.3, D * 0.055);
    const angles = SHAPES[shape].prongs ? SHAPES[shape].prongs.map((x) => (x * Math.PI) / 180)
      : Array.from({ length: prongs }, (_, i) => Math.PI / prongs + (i * 2 * Math.PI) / prongs);
    const tipY = D * crownY(0.44) + pr * 0.55;
    angles.forEach((a) => {
      const f = reach(shape, a);
      // radius r (fraction of D) follows the outline; out (mm) is added unstretched
      const at = (r, y, out = 0) => new THREE.Vector3(Math.cos(a) * (r * D * f + out), y, Math.sin(a) * (r * D * f + out));
      const pts = [at(0.18, baseY), at(0.4, -D * 0.32), at(0.5, -D * 0.02, pr * 0.85), at(0.44, tipY)];
      g.add(this.tube(pts, pr));
      g.add(this.ball(pts[3], pr * 1.08));
    });
    // gallery rail under the stone, following its outline
    g.add(this.tube(outlineLoop(shape, D * 0.68, 0, -D * 0.3), pr * 0.8, this.headMetal, true));
    if (halo) this.addHalo(g, D, halo, pr * 1.15 + 0.05);
    return g;
  }
  // small white stones round the centre stone, evenly spaced along its outline, in a metal frame with beads between
  // them; a multiple of 4 so the corner prongs fall in gaps
  addHalo(g, D, d, gap) {
    const mat = this.v.gemMaterial('accent'), shape = this.shape, y = -D * 0.05;
    const path = outlineLoop(shape, D, d / 2 + gap, y, 360);
    const cum = [0];
    for (let i = 1; i <= path.length; i++) cum.push(cum[i - 1] + path[i % path.length].distanceTo(path[i - 1]));
    const L = cum[path.length], n = Math.max(8, 4 * Math.floor(L / (d + 0.16) / 4));
    const at = (s) => {
      s = ((s % L) + L) % L;
      let i = 1;
      while (cum[i] < s) i++;
      const t = (s - cum[i - 1]) / (cum[i] - cum[i - 1]);
      return path[i - 1].clone().lerp(path[i % path.length], t);
    };
    const start = (L * 45) / 360;
    for (let i = 0; i < n; i++) {
      const p = at(start + ((i + 0.5) * L) / n);
      const s = this.stone(d, mat, 'round');
      s.position.copy(p);
      g.add(s);
      const b = at(start + (i * L) / n);
      b.y += d * crownY(0.45) * 0.6;
      g.add(this.ball(b, d * 0.17));
    }
    g.add(this.tube(outlineLoop(shape, D, d / 2 + gap, y - d * 0.28), d * 0.34, this.headMetal, true));
    g.add(this.tube(outlineLoop(shape, D, d + gap + d * 0.02, y + d * 0.02), d * 0.17, this.headMetal, true));
    g.add(this.tube(outlineLoop(shape, D, gap + d * 0.02, y - d * 0.02), d * 0.12, this.headMetal, true));
  }
  // belcher chain: round links along a curve, each turned a quarter turn from the last
  chain(points) {
    const curve = new THREE.CatmullRomCurve3(points);
    const n = Math.round(curve.getLength() / 0.95);
    const geo = new THREE.TorusGeometry(0.42, 0.12, 8, 20);
    geo.scale(1.35, 1, 1);
    const links = new THREE.InstancedMesh(geo, this.metal, n);
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), twist = new THREE.Quaternion(), x = new THREE.Vector3(1, 0, 0);
    for (let i = 0; i < n; i++) {
      const u = (i + 0.5) / n;
      const t = curve.getTangentAt(u);
      q.setFromUnitVectors(x, t);
      twist.setFromAxisAngle(x, i % 2 ? Math.PI / 2 : 0);
      m.compose(curve.getPointAt(u), q.clone().multiply(twist), new THREE.Vector3(1, 1, 1));
      links.setMatrixAt(i, m);
    }
    links.userData.noFit = true;
    return links;
  }
  // ring band: rounded rectangle section turned round the finger (size N, 17.3 mm inside); ring axis = X, top = +Y
  band(Ri = 8.65, t = 1.6, w = 2.0) {
    const c = 0.55, pts = [];
    const arc = (cx, cy, a0) => { for (let i = 0; i <= 6; i++) { const a = a0 + (i / 6) * Math.PI / 2; pts.push(new THREE.Vector2(cx + Math.cos(a) * c, cy + Math.sin(a) * c)); } };
    arc(Ri + t - c, w / 2 - c, 0);
    arc(Ri + c, w / 2 - c, Math.PI / 2);
    arc(Ri + c, -w / 2 + c, Math.PI);
    arc(Ri + t - c, -w / 2 + c, Math.PI * 1.5);
    pts.push(pts[0].clone());
    const geo = new THREE.LatheGeometry(pts, 160);
    geo.rotateZ(Math.PI / 2);
    return new THREE.Mesh(geo, this.metal);
  }
  // rings: long stones lie along the finger (X, "north-south")
  ring(kind) {
    const Ri = 8.65, t = 1.6, w = 2.0, Ro = Ri + t, g = new THREE.Group();
    g.add(this.band(Ri, t, w));
    const seat = (D, theta, opts) => {
      const pivot = new THREE.Group();
      const y = Ro + BRILLIANT.pavilion * D + 0.45;
      const h = this.head(D, { baseY: -(y - Ro) - 0.25, ...opts });
      h.position.y = y;
      pivot.add(h);
      pivot.rotation.x = theta;
      g.add(pivot);
    };
    if (kind === 'solitaire') seat(this.width(1), 0, { prongs: 4 }); // "one centre stone on four claws"
    if (kind === 'halo') seat(this.width(0.6), 0, { halo: this.accent || 1.25 });
    if (kind === 'three-stone') {
      const D = this.width(0.75), d = this.width(0.3, this.sideCt || (this.ct ? this.ct * 0.4 : 0)), gap = 0.45;
      // side stones sit along the band (Z) and lean with it. Turn them out until the nearest girdle edges (and the
      // prongs between them) are a clear gap apart, measured on the curve, not along the arc.
      const half = (x) => (x * reach(this.shape, Math.PI / 2)) / 2 + Math.max(0.3, x * 0.055);
      const Rc = Ro + BRILLIANT.pavilion * D + 0.45, Rs = Ro + BRILLIANT.pavilion * d + 0.45;
      let th = 0.05;
      for (; th < 1.2; th += 0.004) {
        const e1 = new THREE.Vector3(0, Rc, half(D));
        const e2 = new THREE.Vector3(0, Rs * Math.cos(th) + half(d) * Math.sin(th), Rs * Math.sin(th) - half(d) * Math.cos(th));
        if (e2.z - e1.z >= gap && e2.distanceTo(e1) >= gap) break;
      }
      seat(D, 0, {});
      seat(d, th, { mat: this.v.gemMaterial('side') });
      seat(d, -th, { mat: this.v.gemMaterial('side') });
    }
    g.userData.band = { Ri, w };
    g.userData.view = new THREE.Vector3(1, 0.55, 0.85);
    g.userData.ground = -Ro;
    g.userData.lift = 0.15;
    g.userData.zoom = 0.95;
    g.userData.maxPolar = 1.85;
    return g;
  }
  // face-on piece: table toward the viewer (+Z), long axis (and a pear's point) upward
  faceOn(h) {
    h.rotation.set(Math.PI / 2, this.shape === 'round' ? 0 : Math.PI / 2, 0);
    return h;
  }
  // how far the face-on stone (and halo) reaches upward, for placing the bail
  topOf(D, halo) { return (reach(this.shape, 0) * D) / 2 + (halo ? halo + 0.6 : 0); }
  pendant(halo = 0) {
    const g = new THREE.Group(), D = this.width(halo ? 0.5 : 1);
    const h = this.faceOn(this.head(D, { baseY: -D * 0.55, halo }));
    g.add(h);
    const top = this.shape === 'round' ? D / 2 + (halo ? halo + 0.6 : 0) : this.topOf(D, halo);
    const yb = top + 2.6;
    const bail = new THREE.Mesh(new THREE.TorusGeometry(1.5, 0.42, 12, 48), this.headMetal);
    bail.rotation.y = Math.PI / 2;
    bail.position.set(0, yb, -D * 0.12);
    g.add(bail);
    g.add(this.tube([new THREE.Vector3(0, top - 0.6, -D * 0.12), new THREE.Vector3(0, yb - 1.5, -D * 0.12)], 0.36));
    // the chain runs through the bail's hole and rests on its inner top edge
    const z = -D * 0.12, rest = yb + 1.5 - 0.42 - 0.2;
    const V = (x, y, zz) => new THREE.Vector3(x, y, zz);
    g.add(this.chain([V(-13, yb + 34, z - 3), V(-7, yb + 12, z - 1), V(-2.4, yb + 1.6, z), V(0, rest, z), V(2.4, yb + 1.6, z), V(7, yb + 12, z - 1), V(13, yb + 34, z - 3)]));
    g.userData.view = new THREE.Vector3(0.4, 0.28, 1);
    g.userData.zoom = 1.3;
    return g;
  }
  studs(halo = 0) {
    const g = new THREE.Group(), D = this.width(0.5);
    const across = (reach(this.shape, Math.PI / 2) * D) / 2;
    const spread = across + (halo ? halo + 1 : 0) + 3.5;
    [-1, 1].forEach((s) => {
      const h = this.head(D, { baseY: -D * 0.5, halo });
      const post = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.45, 9, 16), this.headMetal);
      post.position.y = -D * 0.35 - 4.5;
      h.add(post);
      const back = new THREE.Mesh(new THREE.CylinderGeometry(2.4, 2.4, 0.6, 32), this.headMetal);
      back.position.y = -D * 0.35 - 7.5;
      h.add(back);
      this.faceOn(h);
      h.position.x = s * spread;
      g.add(h);
    });
    g.userData.view = new THREE.Vector3(0.4, 0.25, 1);
    g.userData.zoom = 0.95;
    return g;
  }
  build(item) {
    if (item === 'pendant') return this.pendant();
    if (item === 'halo-pendant') return this.pendant(this.accent || 1);
    if (item === 'studs') return this.studs();
    if (item === 'halo-studs') return this.studs(this.accent || 0.9);
    return this.ring(ITEMS[item] ? item : 'solitaire');
  }
}

// Engraving inside the ring band: the text on a thin strip just inside the band, centred at the bottom of the ring
// (where you see it looking into the ring), in darker metal.
function engravingMesh(text, Ri, w) {
  const c = document.createElement('canvas');
  const x = c.getContext('2d');
  const font = 'italic 92px Georgia, "Times New Roman", serif';
  x.font = font;
  const tw = Math.ceil(x.measureText(text).width) + 40;
  c.width = Math.min(4096, tw);
  c.height = 128;
  x.font = font;
  x.fillStyle = '#ffffff';
  x.textAlign = 'center';
  x.textBaseline = 'middle';
  x.fillText(text, c.width / 2, 68);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.wrapS = THREE.RepeatWrapping; // read from inside the ring: mirror back
  tex.repeat.x = -1;
  // keep the letters' proportions; long text gets smaller so it fits the look-inside view (0.85 rad of band: 1.5 ran
  // past both edges of the stage, measured 15 Oct 2026)
  let h = w * 0.55, len = (h * c.width) / c.height;
  const MAX = 0.85;
  if (len / Ri > MAX) { h *= (MAX * Ri) / len; len = MAX * Ri; }
  const thetaLength = len / Ri;
  const geo = new THREE.CylinderGeometry(Ri - 0.004, Ri - 0.004, h, 160, 1, true, Math.PI * 1.5 - thetaLength / 2, thetaLength);
  geo.rotateZ(Math.PI / 2);
  const mat = new THREE.MeshStandardMaterial({
    color: 0x3a3229, alphaMap: tex, transparent: true, metalness: 0.35, roughness: 0.65, side: THREE.BackSide,
    polygonOffset: true, polygonOffsetFactor: -2, depthWrite: false,
  });
  return new THREE.Mesh(geo, mat);
}

// Decodes a model module from tools/build-swirl-models.ps1: per part quantised Int16 positions (lo + q x scale) and
// triangle indices, both base64. Normals are creased at 75 degrees (55 left curved metal in hard-edged blocks, Justin 20 Oct 2026: "shadows are still very blocky"): smooth across the simplified surfaces, crisp at the CAD's real (square) edges.
const b64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
// Soft shade where metal is hidden by metal (crevices, under the swirls, where parts meet): ambient occlusion baked
// per vertex when a design loads (21 Oct 2026, live view upgrade; the flat look was the biggest gap to the photos).
// The parts' surfaces are marked in a 0.1 mm voxel grid; from each vertex 14 short rays (up to 2 mm) over the
// hemisphere round its normal count how much nearby metal blocks the view. Written as vertex colour (white = open).
function bakeOcclusion(geos) {
  const h = 0.1, box = new THREE.Box3();
  geos.forEach((g) => { g.computeBoundingBox(); box.union(g.boundingBox); });
  const o = box.min.clone().subScalar(1.6), size = box.getSize(new THREE.Vector3()).addScalar(3.2);
  const nx = Math.ceil(size.x / h), ny = Math.ceil(size.y / h), nz = Math.ceil(size.z / h);
  if (nx * ny * nz > 60e6) return; // too big to bake: leave it unshaded
  const grid = new Uint8Array(nx * ny * nz);
  const mark = (x, y, z) => { const i = Math.floor((x - o.x) / h), j = Math.floor((y - o.y) / h), k = Math.floor((z - o.z) / h); grid[(i * ny + j) * nz + k] = 1; };
  for (const g of geos) {
    const p = g.attributes.position.array, idx = g.index.array;
    for (let t = 0; t < idx.length; t += 3) {
      const a = idx[t] * 3, b = idx[t + 1] * 3, c = idx[t + 2] * 3;
      const len = Math.max(Math.hypot(p[b] - p[a], p[b + 1] - p[a + 1], p[b + 2] - p[a + 2]), Math.hypot(p[c] - p[a], p[c + 1] - p[a + 1], p[c + 2] - p[a + 2]), Math.hypot(p[c] - p[b], p[c + 1] - p[b + 1], p[c + 2] - p[b + 2]));
      const n = Math.max(1, Math.ceil(len / (h * 0.7)));
      for (let u = 0; u <= n; u++) for (let v = 0; v <= n - u; v++) {
        const wu = u / n, wv = v / n, ww = 1 - wu - wv;
        mark(p[a] * ww + p[b] * wu + p[c] * wv, p[a + 1] * ww + p[b + 1] * wu + p[c + 1] * wv, p[a + 2] * ww + p[b + 2] * wu + p[c + 2] * wv);
      }
    }
  }
  const hit = (x, y, z) => { const i = Math.floor((x - o.x) / h), j = Math.floor((y - o.y) / h), k = Math.floor((z - o.z) / h); return i >= 0 && j >= 0 && k >= 0 && i < nx && j < ny && k < nz && grid[(i * ny + j) * nz + k] === 1; };
  // 14 directions over the upper hemisphere (golden spiral), turned to each vertex normal
  const dirs = []; for (let i = 0; i < 14; i++) { const z = 1 - (i + 0.5) / 14, r = Math.sqrt(1 - z * z), a = i * 2.39996; dirs.push(new THREE.Vector3(Math.cos(a) * r, Math.sin(a) * r, z)); }
  const up = new THREE.Vector3(0, 0, 1), q = new THREE.Quaternion(), d = new THREE.Vector3(), nrm = new THREE.Vector3();
  for (const g of geos) {
    g.computeVertexNormals();
    const p = g.attributes.position.array, nn = g.attributes.normal.array, count = p.length / 3, col = new Float32Array(count * 3);
    for (let v = 0; v < count; v++) {
      nrm.set(nn[v * 3], nn[v * 3 + 1], nn[v * 3 + 2]); if (nrm.lengthSq() < 1e-6) { col.fill(1, v * 3, v * 3 + 3); continue; }
      q.setFromUnitVectors(up, nrm.normalize());
      const sx = p[v * 3] + nrm.x * h * 1.8, sy = p[v * 3 + 1] + nrm.y * h * 1.8, sz = p[v * 3 + 2] + nrm.z * h * 1.8;
      let blocked = 0;
      for (const base of dirs) {
        d.copy(base).applyQuaternion(q);
        for (let step = 1; step <= 20; step++) { const t = step * h; if (hit(sx + d.x * t, sy + d.y * t, sz + d.z * t)) { blocked += 1 - (step - 1) / 20 * 0.5; break; } }
      }
      const open = 1 - blocked / dirs.length, c = 0.22 + 0.78 * Math.pow(open, 1.6);
      col[v * 3] = col[v * 3 + 1] = col[v * 3 + 2] = c;
    }
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    g.deleteAttribute('normal');
  }
}

async function decodeDesign(src) {
  const [{ default: mod }, { toCreasedNormals }] = await Promise.all([import(src), import('./bs-vendor-three-buffer-geometry-utils.js')]);
  let minY = Infinity;
  const parts = mod.parts.map((p) => {
    const q = new Uint16Array(b64(p.pos).buffer), pos = new Float32Array(q.length);
    for (let i = 0; i < q.length; i++) pos[i] = p.lo[i % 3] + q[i] * p.scale[i % 3];
    for (let i = 1; i < pos.length; i += 3) minY = Math.min(minY, pos[i]);
    const ib = b64(p.idx).buffer, idx = p.index32 ? new Uint32Array(ib) : new Uint16Array(ib);
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setIndex(new THREE.BufferAttribute(idx, 1));
    return { role: p.role, geo: g };
  });
  try { bakeOcclusion(parts.map((p) => p.geo)); } catch (e) { console.warn('occlusion not baked', e); }
  parts.forEach((p) => { p.geo = toCreasedNormals(p.geo, THREE.MathUtils.degToRad(75)); });
  // seats: the stones, measured on the CAD (tools/swirl-models.json), already in the model's own space
  return { ri: mod.ri, minY, parts, seats: mod.seats || [], view: mod.view };
}

class BsPieceViewer extends HTMLElement {
  // item/shape/ct/side-ct/accent-mm change the model; metal, stone/color and small-stone/small-color only recolour it; engraving re-cuts its text
  static get observedAttributes() { return ['item', 'shape', 'ct', 'side-ct', 'accent-mm', 'metal', 'finish', 'stone', 'color', 'small-stone', 'small-color', 'engraving', 'model', 'design', 'seats']; }

  connectedCallback() {
    if (this.failed) return;
    if (this.renderer) { this.ro.observe(this); this.io.observe(this); return; } // moved back into the page
    try { this.setup(); } catch (e) { this.failed = true; this.showError(); console.error(e); }
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this.ro?.disconnect();
    this.running = false;
  }

  attributeChangedCallback(name, before, after) {
    if (!this.renderer || before === after) return;
    if (name === 'metal' || name === 'finish') { this.applyMetal(); this.kick(); return; }
    if (['stone', 'color', 'small-stone', 'small-color'].includes(name)) { this.applyStone(); this.kick(); return; }
    if (name === 'model' && !after) return;
    // design and seats usually change together (a new Swirl design): one rebuild for both
    if (name === 'design' || name === 'seats') {
      if (!this.pendingDesign) { this.pendingDesign = true; queueMicrotask(() => { this.pendingDesign = false; this.rebuild(); }); }
      return;
    }
    if (name === 'engraving') {
      this.applyEngraving();
      // typing an engraving turns the view to look into the ring; clearing it goes back to the usual view
      if (after && this.piece && this.piece.userData.band) this.lookInside(); else if (this.piece) this.frame();
      this.kick();
      return;
    }
    this.rebuild();
  }

  showError() {
    this.innerHTML = '<p class="bs-viewer__error">This 3D preview needs a browser with WebGL.</p>';
  }

  setup() {
    const canvas = document.createElement('canvas');
    canvas.className = 'bs-viewer__canvas';
    this.prepend(canvas);
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
    // neutral tone mapping keeps the gold its colour (ACES washed it to cream and greyed the silver)
    renderer.toneMapping = THREE.NeutralToneMapping;
    renderer.toneMappingExposure = 1.0;
    renderer.setClearColor(0x000000, 0);
    this.renderer = renderer;

    // Studio light: a jewellery studio lights the metal (PMREM); the stones see their own bright studio (HDR cube map).
    const pmrem = new THREE.PMREMGenerator(renderer);
    this.scene = new THREE.Scene();
    this.scene.environment = pmrem.fromScene(metalStudio(), 0.02).texture;
    const cubeRT = new THREE.WebGLCubeRenderTarget(512, { type: THREE.HalfFloatType });
    new THREE.CubeCamera(0.1, 100, cubeRT).update(renderer, gemStudio());
    this.envCube = cubeRT.texture;
    pmrem.dispose();

    this.gems = {}; // stone geometry per shape, made once and shared
    this.metalMaterial = new THREE.MeshPhysicalMaterial({ metalness: 1, roughness: 0.06, envMapIntensity: 1 });
    this.headMaterial = new THREE.MeshPhysicalMaterial({ metalness: 1, roughness: 0.06, envMapIntensity: 1 });
    this.gemMaterials = [];

    this.camera = new THREE.PerspectiveCamera(30, 1, 0.1, 1000);
    const controls = new OrbitControls(this.camera, canvas);
    controls.enableDamping = true;
    controls.enablePan = false;
    canvas.style.touchAction = 'pan-y'; // OrbitControls sets 'none'; a vertical swipe should still scroll the page
    controls.autoRotate = true;
    controls.autoRotateSpeed = 1.1;
    controls.addEventListener('start', () => { controls.autoRotate = false; this.customView = true; clearTimeout(this.idle); });
    controls.addEventListener('end', () => { this.idle = setTimeout(() => { controls.autoRotate = !this.hasAttribute('still'); }, 5000); });
    this.controls = controls;

    const shadow = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ map: shadowTexture(), transparent: true, depthWrite: false }));
    shadow.rotation.x = -Math.PI / 2;
    this.shadow = shadow;
    this.scene.add(shadow);

    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(this);
    this.io = new IntersectionObserver((es) => { this.visible = es.some((e) => e.isIntersecting); if (this.visible) this.loop(); });
    this.io.observe(this);
    if (this.hasAttribute('data-allow-drop')) this.enableDrop();
    this.rebuild();
  }

  // role: 'centre', 'side' (three-stone side stones) or 'accent' (halo). Small stones (side + accent) take the
  // small-stone / small-color attributes; without them halo stones are white cubic zirconia and side stones match
  // the centre stone.
  gemMaterial(role = 'centre') {
    if (role === true) role = 'accent';
    const s = this.lookFor(role);
    const m = new THREE.ShaderMaterial({
      uniforms: {
        envMap: { value: this.envCube }, tint: { value: new THREE.Color(s.color) },
        ior: { value: s.ior }, spread: { value: s.spread }, body: { value: s.body }, boost: { value: s.boost }, density: { value: s.density || 0 },
        // facets: set per stone before it draws (gemPlanesHook); fire only on centre stones (small ones are a few pixels)
        planes: { value: new Float32Array(GEM_MAXP * 4) }, nPlanes: { value: 0 }, fireOn: { value: role === 'accent' ? 0 : 1 },
      },
      vertexShader: GEM_VERT, fragmentShader: GEM_FRAG, side: THREE.DoubleSide,
    });
    m.userData.role = role;
    this.gemMaterials.push(m);
    return m;
  }

  lookFor(role) {
    const small = this.getAttribute('small-stone');
    if (role === 'centre' || (role === 'side' && !small)) return this.stoneLook();
    if (!small) return { ...STONES.cz };
    return this.stoneLook(small, this.getAttribute('small-color'));
  }

  // The stone's look. A builder colour (color attribute) replaces the stone's own colour; a coloured version of a
  // colourless stone (pink CZ, yellow lab diamond) gets a body colour. White / colourless choices keep the stone's own.
  stoneLook(id = this.getAttribute('stone'), hex = this.getAttribute('color')) {
    const s = { ...(STONES[id] || STONES['lab-diamond']) };
    if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return s;
    const hsl = {};
    new THREE.Color(hex).getHSL(hsl);
    if (hsl.s < 0.18 && hsl.l > 0.8) return s;
    // near-neutral dark colours (black CZ) are a darker clear stone, not a tint (a slight cast would turn violet)
    if (hsl.s < 0.25) return Object.assign(s, { boost: 0.04 + 0.9 * hsl.l * hsl.l }); // black CZ reads black (was grey, 20 Oct)
    s.color = hex;
    if (!s.body) s.body = 1;
    // darker colours (London topaz, black CZ) need less light and deeper absorption, or they read pale
    s.boost = Math.min(s.boost, 0.3 + 0.9 * hsl.l);
    s.density = Math.max(s.density || 0, 1.2 + 3 * (1 - hsl.l));
    // light, pastel colours (pink, champagne, peach) absorb gently and keep some white: at the dark-colour depth the
    // light's long path through the facets drained green and blue and pink read as ruby (Justin, 20 Oct 2026)
    const seen = {}; new THREE.Color(hex).getHSL(seen, THREE.SRGBColorSpace); // lightness as seen (sRGB), not linear
    // mixed hues (purple, violet, yellow-green): deep absorption drains the weaker channel and the hue drifts
    // (amethyst read as sapphire, review 20 Oct 2026), so keep them shallow; very dark colours go darker, not brighter
    const lin = new THREE.Color(hex), ch = [lin.r, lin.g, lin.b].sort((a, b) => b - a);
    if (ch[0] > 0 && ch[1] / ch[0] > 0.2) s.density = Math.min(s.density, 0.6);
    if (seen.l < 0.3) s.boost = Math.min(s.boost, 0.15 + 1.2 * seen.l);
    if (seen.l >= 0.65) { s.density = 0; s.body = 0.8; s.boost = Math.max(s.boost, 1.1); }
    return s;
  }

  applyStone() {
    const looks = {};
    this.gemMaterials.forEach((m) => {
      const role = m.userData.role || 'centre';
      const s = looks[role] || (looks[role] = this.lookFor(role));
      const u = m.uniforms;
      u.tint.value.set(s.color);
      u.ior.value = s.ior;
      u.spread.value = s.spread;
      u.body.value = s.body;
      u.boost.value = s.boost;
      u.density.value = s.density || 0;
    });
  }

  // a picture of the current view for sharing (the builder's Share): drawn and read in the same frame, PNG data URL
  snapshot() {
    if (!this.renderer) return null;
    // drawn at about 1080 px across the short side (the share picture's size), then the screen's resolution again
    const r = this.renderer, pr = r.getPixelRatio(), el = r.domElement, short = Math.min(el.clientWidth, el.clientHeight) || 1;
    let url = null;
    try { r.setPixelRatio(Math.min(4, Math.max(pr, 1080 / short))); r.render(this.scene, this.camera); url = el.toDataURL('image/png'); }
    catch (e) { url = null; }
    finally { r.setPixelRatio(pr); r.render(this.scene, this.camera); }
    return url;
  }

  applyMetal() {
    const m = METALS[this.getAttribute('metal')] || METALS['925'];
    this.metalMaterial.color.set(m.color);
    // finish on the sterling silver parts (gold parts stay polished): brushed = fine satin, matt = soft satin,
    // oxidised = darkened antique silver
    const fin = this.getAttribute('finish'), silver = (c) => c === '#eef0f2';
    const LOOKS = { brushed: { rough: 0.32 }, matt: { rough: 0.55 }, oxidised: { color: '#a3a5aa', rough: 0.3 } };
    const look = (c) => (silver(c) && LOOKS[fin]) || null;
    const set = (mat, l, c) => { mat.color.set((l && l.color) || c); mat.roughness = l ? l.rough : m.rough; mat.anisotropy = (l && l.aniso) || 0; mat.needsUpdate = true; };
    set(this.metalMaterial, look(m.color), m.color);
    set(this.headMaterial, look(m.head || m.color), m.head || m.color);
  }

  clearPiece() {
    if (!this.piece) return;
    this.scene.remove(this.piece);
    const shared = new Set(Object.values(this.gems));
    // CAD geometry is kept for the next visit to that design (this.designs)
    this.piece.traverse((o) => { if (o.isMesh && !shared.has(o.geometry) && !o.userData.cad) o.geometry.dispose(); if (o.userData.engraving) { o.material.alphaMap.dispose(); o.material.dispose(); } });
    this.gemMaterials.forEach((m) => m.dispose());
    this.gemMaterials = [];
    this.piece = null;
  }

  async rebuild() {
    this.applyMetal();
    const design = this.getAttribute('design');
    if (design) return this.loadDesign(design);
    const model = this.getAttribute('model');
    if (model) return this.loadModel(model);
    const key = ['item', 'shape', 'ct', 'side-ct', 'accent-mm'].map((a) => this.getAttribute(a)).join('|');
    if (key === this.builtKey && this.piece) { this.kick(); return; }
    this.clearPiece();
    this.builtKey = key;
    this.setPiece(new PieceBuilder(this).build(this.getAttribute('item') || 'solitaire'));
  }

  setPiece(piece) {
    this.piece = piece;
    // metal takes the baked shade (vertex colour) only when every metal mesh has one (CAD designs); others stay plain
    let shaded = false, plain = false;
    piece.traverse((o) => { if (o.isMesh && (o.material === this.metalMaterial || o.material === this.headMaterial)) { if (o.geometry.attributes.color) shaded = true; else plain = true; } });
    for (const m of [this.metalMaterial, this.headMaterial]) { const want = shaded && !plain; if (m.vertexColors !== want) { m.vertexColors = want; m.needsUpdate = true; } }
    this.scene.add(piece);
    piece.updateMatrixWorld(true);
    this.applyEngraving();
    this.frame();
  }

  // camera inside the ring's opening, looking down at the engraving (bottom inside of the band)
  lookInside() {
    const { Ri } = this.piece.userData.band;
    this.customView = true;
    this.controls.autoRotate = false;
    clearTimeout(this.idle);
    this.controls.target.set(0, -Ri, 0);
    this.camera.position.set(Ri * 1.05, Ri * 0.25, 0.5);
    this.controls.minDistance = 4;
    this.controls.update();
  }

  gemGeometry(shape) { return this.gems[shape] || (this.gems[shape] = gemGeometry(shape)); }

  // text inside the band (rings only); replaced in place when the text changes
  applyEngraving() {
    if (!this.piece) return;
    const old = this.piece.children.find((o) => o.userData.engraving);
    if (old) { this.piece.remove(old); old.geometry.dispose(); old.material.alphaMap.dispose(); old.material.dispose(); }
    const text = (this.getAttribute('engraving') || '').trim(), band = this.piece.userData.band;
    if (!text || !band) return;
    const m = engravingMesh(text, band.Ri, band.w);
    m.userData.engraving = true;
    m.userData.noFit = true;
    this.piece.add(m);
  }

  // fit the camera to the piece at any width; chains are left out of the fit
  frame() {
    this.customView = false;
    // the default view always turns (looking at the engraving stops it; clearing the engraving comes back here),
    // except with the still attribute (preview/swirl-check.html: the model held at its photo angle)
    clearTimeout(this.idle);
    this.controls.autoRotate = !this.hasAttribute('still');
    const box = new THREE.Box3();
    this.piece.traverse((o) => { if (o.isMesh && !o.userData.noFit && !this.isNoFit(o)) box.expandByObject(o); });
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    this.fit = sphere;
    const ground = this.piece.userData.ground;
    this.shadow.visible = ground != null;
    if (ground != null) {
      this.shadow.position.set(sphere.center.x, ground - 0.02, sphere.center.z);
      this.shadow.scale.setScalar(sphere.radius * 1.3);
    }
    this.view = (this.piece.userData.view || new THREE.Vector3(1, 0.5, 1)).clone().normalize();
    this.controls.maxPolarAngle = this.piece.userData.maxPolar || Math.PI;
    this.controls.target.copy(sphere.center).add(new THREE.Vector3(0, sphere.radius * (this.piece.userData.lift || 0), 0));
    this.placeCamera();
  }

  isNoFit(o) { for (let p = o; p; p = p.parent) if (p.userData.noFit) return true; return false; }

  placeCamera() {
    if (!this.fit) return;
    const cam = this.camera;
    const vf = THREE.MathUtils.degToRad(cam.fov);
    const hf = 2 * Math.atan(Math.tan(vf / 2) * cam.aspect);
    const dist = (this.fit.radius * (this.piece.userData.zoom || 1.05)) / Math.sin(Math.min(vf, hf) / 2);
    cam.position.copy(this.controls.target).addScaledVector(this.view, dist);
    cam.near = dist / 100;
    cam.far = dist * 10;
    cam.updateProjectionMatrix();
    this.controls.minDistance = dist * 0.45;
    this.controls.maxDistance = dist * 1.8;
    this.controls.update();
    this.kick();
  }

  // A size change keeps the customer's view (turned, zoomed or looking at the engraving); only the default view is
  // re-fitted, so the piece stays framed when the stage grows or shrinks.
  resize() {
    const w = Math.max(1, this.clientWidth), h = Math.max(1, this.clientHeight);
    if (w === this.lastW && h === this.lastH) return;
    this.lastW = w;
    this.lastH = h;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    if (this.customView) { this.camera.updateProjectionMatrix(); this.kick(); } else this.placeCamera();
  }

  kick() { if (this.visible) this.loop(); }

  loop() {
    if (this.running) return;
    this.running = true;
    const tick = () => {
      if (!this.visible || !this.isConnected) { this.running = false; return; }
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  // A Swirl design: a model module made by tools/build-swirl-models.ps1 from Beautiful Selection's CAD (metal only;
  // finger axis X, head up +Y, mm) plus its stones (seats measured on the CAD, tools/swirl-models.json; a seats attribute overrides them):
  // { shape, w (mm across), l (mm long, optional), p: [x,y,z] girdle centre, n: [x,y,z] table direction,
  //   a: turn about n in degrees (long axis), role: 'centre' | 'accent' }. Part role "band" takes the metal,
  //   "head" the head metal (gold on two tone). Geometry is decoded once per design and kept.
  async loadDesign(src) {
    const key = `${src}|${this.getAttribute('seats')}`;
    if (key === this.builtKey && this.piece) { this.kick(); return; }
    this.builtKey = key;
    const ticket = (this.ticket = (this.ticket || 0) + 1);
    this.designs = this.designs || {};
    let d = this.designs[src];
    if (!d) {
      this.classList.add('is-loading');
      try { d = this.designs[src] = await decodeDesign(src); } catch (e) {
        // a failed download must not stick: choosing the design again tries again
        console.error('bs-piece-viewer: could not load', src, e);
        if (ticket === this.ticket) this.builtKey = null;
        return;
      } finally { this.classList.remove('is-loading'); }
    }
    if (ticket !== this.ticket) return; // a newer design was chosen while this one loaded
    this.clearPiece();
    const g = new THREE.Group();
    d.parts.forEach((p) => {
      const m = new THREE.Mesh(p.geo, p.role === 'head' ? this.headMaterial : this.metalMaterial);
      m.userData.cad = true;
      g.add(m);
    });
    // the model's own seats (measured on the CAD); a seats attribute, when given, replaces them (testing)
    let seats = d.seats;
    try { const own = JSON.parse(this.getAttribute('seats') || '[]'); if (own.length) seats = own; } catch (e) { console.warn('bs-piece-viewer: bad seats', e); }
    const up = new THREE.Vector3(0, 1, 0);
    seats.forEach((s) => {
      const shape = SHAPES[s.shape] ? s.shape : 'round';
      const m = new THREE.Mesh(this.gemGeometry(shape), this.gemMaterial(s.role === 'accent' ? 'accent' : 'centre'));
      m.onBeforeRender = gemPlanesHook;
      m.userData.gem = true;
      const w = +s.w || 1;
      // the outline already makes long shapes longer; l (if given) stretches to the seat's true length
      const natural = (SHAPES[shape].length || 1) * w;
      m.scale.set(s.l ? (s.l / natural) * w : w, w, w);
      const n = new THREE.Vector3(...(s.n || [0, 1, 0])).normalize();
      m.quaternion.setFromUnitVectors(up, n).multiply(new THREE.Quaternion().setFromAxisAngle(up, THREE.MathUtils.degToRad(s.a || 0)));
      m.position.set(...(s.p || [0, 0, 0]));
      g.add(m);
    });
    // looking down onto the setting at an angle, so the centre stone shows (it turns round its finger axis' upright)
    g.userData.ground = d.minY;
    // (each design opens at the angle of its stock photo: tools/swirl-models.json "view")
    g.userData.view = d.view ? new THREE.Vector3(...d.view) : new THREE.Vector3(0.45, 1, 0.75);
    g.userData.band = { Ri: d.ri, w: 2.4 };
    g.userData.zoom = 1.0;
    this.setPiece(g);
  }

  // CAD files: glTF/GLB keep their mesh names (stones by name); STL is one mesh, shown in metal
  async loadModel(src, name = src) {
    this.clearPiece();
    this.builtKey = null;
    const ext = String(name).split('?')[0].split('.').pop().toLowerCase();
    let obj;
    if (ext === 'stl') {
      const { STLLoader } = await import('./bs-vendor-three-stl-loader.js');
      const geo = await new STLLoader().loadAsync(src);
      geo.computeVertexNormals();
      obj = new THREE.Mesh(geo, this.metalMaterial);
    } else {
      const { GLTFLoader } = await import('./bs-vendor-three-gltf-loader.js');
      obj = (await new GLTFLoader().loadAsync(src)).scene;
      const gem = this.gemMaterial();
      obj.traverse((o) => {
        if (!o.isMesh) return;
        if (/stone|gem|diamond|\bcz\b|moiss/i.test(o.name)) { o.material = gem; o.userData.gem = true; o.onBeforeRender = gemPlanesHook; } else o.material = this.metalMaterial;
      });
    }
    const g = new THREE.Group();
    g.add(obj);
    const box = new THREE.Box3().setFromObject(g);
    g.userData.ground = box.min.y;
    g.userData.view = new THREE.Vector3(1, 0.55, 0.85);
    this.setPiece(g);
    this.dispatchEvent(new CustomEvent('bs-viewer:model', { bubbles: true, detail: { name } }));
  }

  enableDrop() {
    this.addEventListener('dragover', (e) => { e.preventDefault(); this.classList.add('is-drop'); });
    this.addEventListener('dragleave', () => this.classList.remove('is-drop'));
    this.addEventListener('drop', (e) => {
      e.preventDefault();
      this.classList.remove('is-drop');
      const f = e.dataTransfer.files[0];
      if (!f || !/\.(glb|gltf|stl)$/i.test(f.name)) return;
      const url = URL.createObjectURL(f);
      this.loadModel(url, f.name).finally(() => URL.revokeObjectURL(url));
    });
  }
}

// Demo controls: draws the Piece / Metal / Stone buttons from the data above and sets the viewer's attributes.
const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
class BsViewerDemo extends HTMLElement {
  connectedCallback() {
    this.viewer = this.querySelector('bs-piece-viewer');
    const box = this.querySelector('[data-controls]');
    if (!this.viewer || !box || box.childElementCount) return;
    const group = (key, title, entries, swatch) => {
      const cur = this.viewer.getAttribute(key);
      const btns = entries.map(([id, v]) => {
        const label = typeof v === 'string' ? v : v.label;
        const dot = swatch ? `<span class="bs-viewer-demo__dot" style="--dot:${esc(v.color)}"></span>` : '';
        return `<button type="button" class="bs-viewer-demo__btn" data-set="${key}" data-value="${esc(id)}" aria-pressed="${id === cur}">${dot}${esc(label)}</button>`;
      }).join('');
      return `<fieldset class="bs-viewer-demo__group"><legend>${title}</legend><div class="bs-viewer-demo__btns">${btns}</div></fieldset>`;
    };
    box.innerHTML = group('item', 'Piece', Object.entries(ITEMS)) + group('metal', 'Metal', Object.entries(METALS), true)
      + group('stone', 'Stone', Object.entries(STONES), true);
    this.addEventListener('click', (e) => {
      const b = e.target.closest('[data-set]');
      if (!b) return;
      this.viewer.removeAttribute('model');
      this.viewer.setAttribute(b.dataset.set, b.dataset.value);
      this.querySelectorAll(`[data-set="${b.dataset.set}"]`).forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    });
    this.addEventListener('bs-viewer:model', () => {
      this.querySelectorAll('[data-set="item"]').forEach((x) => x.setAttribute('aria-pressed', 'false'));
    });
  }
}

if (!customElements.get('bs-piece-viewer')) customElements.define('bs-piece-viewer', BsPieceViewer);
if (!customElements.get('bs-viewer-demo')) customElements.define('bs-viewer-demo', BsViewerDemo);
